// SPDX-FileCopyrightText: 2017-2026 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

// GENERATED FILE: no manual modifications

import type { Caretakers } from './daycare'
import FiniteDateRange from '../../finite-date-range'
import type { GroupId } from './shared'
import HelsinkiDateTime from '../../helsinki-date-time'
import type { JsonOf } from '../../json'
import LocalDate from '../../local-date'
import type { PersonId } from './shared'

/**
* Generated from evaka.core.occupancy.ChildCapacityPoint
*/
export interface ChildCapacityPoint {
  capacity: number
  time: HelsinkiDateTime
}

/**
* Generated from evaka.core.occupancy.ChildOccupancyAttendance
*/
export interface ChildOccupancyAttendance {
  arrived: HelsinkiDateTime
  capacity: number
  childId: PersonId
  departed: HelsinkiDateTime | null
}

/**
* Generated from evaka.core.occupancy.OccupancyController.GetUnitOccupanciesForDayBody
*/
export interface GetUnitOccupanciesForDayBody {
  date: LocalDate
  groupIds: GroupId[] | null
}

/**
* Generated from evaka.core.occupancy.OccupancyPeriod
*/
export interface OccupancyPeriod {
  caretakers: number | null
  headcount: number
  percentage: number | null
  period: FiniteDateRange
  sum: number
}

/**
* Generated from evaka.core.occupancy.OccupancyPoint
*/
export interface OccupancyPoint {
  childCapacity: number
  occupancyRatio: number | null
  staffCapacity: number
  time: HelsinkiDateTime
}

/**
* Generated from evaka.core.occupancy.OccupancyResponse
*/
export interface OccupancyResponse {
  max: OccupancyPeriod | null
  min: OccupancyPeriod | null
  occupancies: OccupancyPeriod[]
}

/**
* Generated from evaka.core.occupancy.OccupancyResponseSpeculated
*/
export interface OccupancyResponseSpeculated {
  max3Months: OccupancyValues | null
  max3MonthsSpeculated: OccupancyValues | null
  max6Months: OccupancyValues | null
  max6MonthsSpeculated: OccupancyValues | null
}

/**
* Generated from evaka.core.occupancy.OccupancyType
*/
export type OccupancyType =
  | 'DRAFT'
  | 'PLANNED'
  | 'CONFIRMED'
  | 'REALIZED'

/**
* Generated from evaka.core.occupancy.OccupancyValues
*/
export interface OccupancyValues {
  caretakers: number | null
  headcount: number
  percentage: number | null
  sum: number
  sumOver3y: number
  sumUnder3y: number
}

/**
* Generated from evaka.core.occupancy.RealtimeOccupancy
*/
export interface RealtimeOccupancy {
  childAttendances: ChildOccupancyAttendance[]
  childCapacitySumSeries: ChildCapacityPoint[]
  occupancySeries: OccupancyPoint[]
  staffAttendances: StaffOccupancyAttendance[]
  staffCapacitySumSeries: StaffCapacityPoint[]
}

/**
* Generated from evaka.core.occupancy.StaffCapacityPoint
*/
export interface StaffCapacityPoint {
  capacity: number
  time: HelsinkiDateTime
}

/**
* Generated from evaka.core.occupancy.StaffOccupancyAttendance
*/
export interface StaffOccupancyAttendance {
  arrived: HelsinkiDateTime
  capacity: number
  departed: HelsinkiDateTime | null
}

/**
* Generated from evaka.core.occupancy.UnitOccupancies
*/
export interface UnitOccupancies {
  caretakers: Caretakers
  confirmed: OccupancyResponse
  draft: OccupancyResponse | null
  planned: OccupancyResponse
  realized: OccupancyResponse
}


export function deserializeJsonChildCapacityPoint(json: JsonOf<ChildCapacityPoint>): ChildCapacityPoint {
  return {
    ...json,
    time: HelsinkiDateTime.parseIso(json.time)
  }
}


export function deserializeJsonChildOccupancyAttendance(json: JsonOf<ChildOccupancyAttendance>): ChildOccupancyAttendance {
  return {
    ...json,
    arrived: HelsinkiDateTime.parseIso(json.arrived),
    departed: (json.departed != null) ? HelsinkiDateTime.parseIso(json.departed) : null
  }
}


export function deserializeJsonGetUnitOccupanciesForDayBody(json: JsonOf<GetUnitOccupanciesForDayBody>): GetUnitOccupanciesForDayBody {
  return {
    ...json,
    date: LocalDate.parseIso(json.date)
  }
}


export function deserializeJsonOccupancyPeriod(json: JsonOf<OccupancyPeriod>): OccupancyPeriod {
  return {
    ...json,
    period: FiniteDateRange.parseJson(json.period)
  }
}


export function deserializeJsonOccupancyPoint(json: JsonOf<OccupancyPoint>): OccupancyPoint {
  return {
    ...json,
    time: HelsinkiDateTime.parseIso(json.time)
  }
}


export function deserializeJsonOccupancyResponse(json: JsonOf<OccupancyResponse>): OccupancyResponse {
  return {
    ...json,
    max: (json.max != null) ? deserializeJsonOccupancyPeriod(json.max) : null,
    min: (json.min != null) ? deserializeJsonOccupancyPeriod(json.min) : null,
    occupancies: json.occupancies.map(e => deserializeJsonOccupancyPeriod(e))
  }
}


export function deserializeJsonRealtimeOccupancy(json: JsonOf<RealtimeOccupancy>): RealtimeOccupancy {
  return {
    ...json,
    childAttendances: json.childAttendances.map(e => deserializeJsonChildOccupancyAttendance(e)),
    childCapacitySumSeries: json.childCapacitySumSeries.map(e => deserializeJsonChildCapacityPoint(e)),
    occupancySeries: json.occupancySeries.map(e => deserializeJsonOccupancyPoint(e)),
    staffAttendances: json.staffAttendances.map(e => deserializeJsonStaffOccupancyAttendance(e)),
    staffCapacitySumSeries: json.staffCapacitySumSeries.map(e => deserializeJsonStaffCapacityPoint(e))
  }
}


export function deserializeJsonStaffCapacityPoint(json: JsonOf<StaffCapacityPoint>): StaffCapacityPoint {
  return {
    ...json,
    time: HelsinkiDateTime.parseIso(json.time)
  }
}


export function deserializeJsonStaffOccupancyAttendance(json: JsonOf<StaffOccupancyAttendance>): StaffOccupancyAttendance {
  return {
    ...json,
    arrived: HelsinkiDateTime.parseIso(json.arrived),
    departed: (json.departed != null) ? HelsinkiDateTime.parseIso(json.departed) : null
  }
}


export function deserializeJsonUnitOccupancies(json: JsonOf<UnitOccupancies>): UnitOccupancies {
  return {
    ...json,
    confirmed: deserializeJsonOccupancyResponse(json.confirmed),
    draft: (json.draft != null) ? deserializeJsonOccupancyResponse(json.draft) : null,
    planned: deserializeJsonOccupancyResponse(json.planned),
    realized: deserializeJsonOccupancyResponse(json.realized)
  }
}
